import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays } from 'date-fns';

import {
  getRepairOrder,
  updateRepairOrder
} from '../redux/actions/repairOrderActions';
import {
  getActivePaymentModes
} from '../redux/actions/paymentModeActions';
import {
  getCustomers,
  createCustomer
} from '../redux/actions/customerActions';

const EditRepairOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Redux state
  const { currentRepairOrder, loading, error, success } = useSelector(state => state.repairOrders);
  const { activePaymentModes } = useSelector(state => state.paymentModes);
  const { customers } = useSelector(state => state.customers);

  // Form state
  const [formData, setFormData] = useState({
    customer_id: '',
    item_description: '',
    item_weight: '',
    item_type: '',
    repair_type: '',
    estimated_cost: '',
    delivery_date: null,
    notes: ''
  });

  // Customer dialog state
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: ''
  });

  // Payment state
  const [makeAdvancePayment, setMakeAdvancePayment] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date(),
    payment_amount: '',
    notes: ''
  });
  const [selectedPaymentModes, setSelectedPaymentModes] = useState([]);

  // Payment mode icons mapping
  const paymentIcons = {
    cash: '💰',
    'credit_card': '💳',
    'debit_card': '💳',
    'net_banking': '🏦',
    upi: '📱',
    cheque: '📄',
    chit: '🎫',
    'old_purchase': '💎'
  };

  // Load repair order data
  useEffect(() => {
    if (id) {
      dispatch(getRepairOrder(id));
    }
  }, [dispatch, id]);

  // Load initial data
  useEffect(() => {
    dispatch(getActivePaymentModes());
    dispatch(getCustomers());
  }, [dispatch]);

  // Update form data when repair order is loaded
  useEffect(() => {
    if (currentRepairOrder) {
      setFormData({
        customer_id: currentRepairOrder.customer_id || '',
        item_description: currentRepairOrder.item_description || '',
        item_weight: currentRepairOrder.item_weight || '',
        item_type: currentRepairOrder.item_type || '',
        repair_type: currentRepairOrder.repair_type || '',
        estimated_cost: currentRepairOrder.estimated_cost || '',
        delivery_date: currentRepairOrder.delivery_date ? new Date(currentRepairOrder.delivery_date) : null,
        notes: currentRepairOrder.notes || ''
      });

      // Populate existing payments
      if (currentRepairOrder.payments && currentRepairOrder.payments.length > 0) {
        const existingPayments = currentRepairOrder.payments.map(payment => ({
          payment_mode_code: payment.payment_mode_code,
          amount: payment.amount,
          payment_date: payment.payment_date
        }));
        setSelectedPaymentModes(existingPayments);
        setMakeAdvancePayment(true);
      }
    }
  }, [currentRepairOrder]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle payment mode selection
  const handlePaymentModeChange = (modeId, amount) => {
    setSelectedPaymentModes(prev => {
      const existing = prev.find(p => p.payment_mode_code === modeId);
      if (existing) {
        if (amount === '') {
          return prev.filter(p => p.payment_mode_code !== modeId);
        }
        return prev.map(p => 
          p.payment_mode_code === modeId 
            ? { ...p, amount: parseFloat(amount) || 0 }
            : p
        );
      } else if (amount !== '') {
        return [...prev, {
          payment_mode_code: modeId,
          amount: parseFloat(amount) || 0
        }];
      }
      return prev;
    });
  };

  // Get payment amount for a mode
  const getPaymentAmount = (modeId) => {
    const payment = selectedPaymentModes.find(p => p.payment_mode_code === modeId);
    return payment ? payment.amount.toString() : '';
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        delivery_date: formData.delivery_date ? format(formData.delivery_date, 'yyyy-MM-dd') : null
      };

      // Only include payments if advance payment is enabled and there are payments
      if (makeAdvancePayment && selectedPaymentModes.length > 0) {
        submitData.payments = selectedPaymentModes.map(payment => ({
          ...payment,
          payment_date: payment.payment_date || format(paymentData.payment_date, 'yyyy-MM-dd')
        }));
      }

      const result = await dispatch(updateRepairOrder(id, submitData));
      if (result.success) {
        navigate('/repair-orders');
      }
    } catch (error) {
      console.error('Error updating repair order:', error);
    }
  };

  // Handle customer creation
  const handleCreateCustomer = async () => {
    try {
      const result = await dispatch(createCustomer(newCustomer));
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          customer_id: result.data.id
        }));
        setOpenCustomerDialog(false);
        setNewCustomer({
          name: '',
          phone: '',
          email: '',
          address: '',
          gstin: ''
        });
        dispatch(getCustomers()); // Refresh customers list
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const safeFormatCurrency = (value) => {
    const num = Number(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  if (loading && !currentRepairOrder) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !currentRepairOrder) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/repair-orders')}
        >
          Back to Repair Orders
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/repair-orders')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              Edit Repair Order
            </Typography>
            {currentRepairOrder && (
              <Chip
                label={currentRepairOrder.order_number}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Customer Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined" required>
                        <InputLabel>Customer</InputLabel>
                        <Select
                          value={formData.customer_id}
                          onChange={(e) => handleInputChange('customer_id', e.target.value)}
                          label="Customer"
                        >
                          {customers?.map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              {customer.name} - {customer.phone}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setOpenCustomerDialog(true)}
                        sx={{ height: '56px' }}
                      >
                        Add New Customer
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Item Details */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Item Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Item Description"
                        variant="outlined"
                        value={formData.item_description}
                        onChange={(e) => handleInputChange('item_description', e.target.value)}
                        required
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Item Weight (gm)"
                        variant="outlined"
                        type="number"
                        step="0.001"
                        value={formData.item_weight}
                        onChange={(e) => handleInputChange('item_weight', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">gm</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Item Type</InputLabel>
                        <Select
                          value={formData.item_type}
                          onChange={(e) => handleInputChange('item_type', e.target.value)}
                          label="Item Type"
                        >
                          <MenuItem value="gold">Gold</MenuItem>
                          <MenuItem value="silver">Silver</MenuItem>
                          <MenuItem value="diamond">Diamond</MenuItem>
                          <MenuItem value="mixed">Mixed</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Repair Type</InputLabel>
                        <Select
                          value={formData.repair_type}
                          onChange={(e) => handleInputChange('repair_type', e.target.value)}
                          label="Repair Type"
                        >
                          <MenuItem value="polishing">Polishing</MenuItem>
                          <MenuItem value="resizing">Resizing</MenuItem>
                          <MenuItem value="stone_setting">Stone Setting</MenuItem>
                          <MenuItem value="chain_repair">Chain Repair</MenuItem>
                          <MenuItem value="cleaning">Cleaning</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Estimated Cost"
                        variant="outlined"
                        type="number"
                        step="0.01"
                        value={formData.estimated_cost}
                        onChange={(e) => handleInputChange('estimated_cost', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DatePicker
                        label="Expected Delivery Date"
                        value={formData.delivery_date}
                        onChange={(date) => handleInputChange('delivery_date', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes"
                        variant="outlined"
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={makeAdvancePayment}
                            onChange={(e) => setMakeAdvancePayment(e.target.checked)}
                          />
                        }
                        label="Make Advance Payment"
                      />
                    </Grid>
                    {makeAdvancePayment && (
                      <Grid item xs={12} md={4}>
                        <DatePicker
                          label="Payment Date"
                          value={paymentData.payment_date}
                          onChange={(date) => setPaymentData(prev => ({ ...prev, payment_date: date }))}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: 'outlined'
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {makeAdvancePayment && (
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      {activePaymentModes?.map((mode) => (
                        <Grid item xs={12} md={6} key={mode.id}>
                          <TextField
                            fullWidth
                            label={`${paymentIcons[mode.code] || '💳'} ${mode.name}`}
                            variant="outlined"
                            type="number"
                            step="0.01"
                            value={getPaymentAmount(mode.code)}
                            onChange={(e) => handlePaymentModeChange(mode.code, e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>
                            }}
                            placeholder="0.00"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Current Payment Summary */}
                  {currentRepairOrder?.payments && currentRepairOrder.payments.length > 0 && (
                    <Paper sx={{ p: 2, mt: 2, bgcolor: '#e3f2fd' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Existing Payments
                      </Typography>
                      {currentRepairOrder.payments.map((payment, index) => {
                        const mode = activePaymentModes?.find(m => m.code === payment.payment_mode_code);
                        return (
                          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {mode?.name || payment.payment_mode_code} ({new Date(payment.payment_date).toLocaleDateString()})
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              ₹{safeFormatCurrency(payment.amount)}
                            </Typography>
                          </Box>
                        );
                      })}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total Existing Payments
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          ₹{safeFormatCurrency(currentRepairOrder.payments.reduce((sum, p) => sum + p.amount, 0))}
                        </Typography>
                      </Box>
                    </Paper>
                  )}

                  {/* New Payment Summary */}
                  {makeAdvancePayment && selectedPaymentModes.length > 0 && (
                    <Paper sx={{ p: 2, mt: 2, bgcolor: '#f9f9f9' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        New Payment Summary
                      </Typography>
                      {selectedPaymentModes.map((payment) => {
                        const mode = activePaymentModes?.find(m => m.code === payment.payment_mode_code);
                        return (
                          <Box key={payment.payment_mode_code} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {paymentIcons[payment.payment_mode_code] || '💳'} {mode?.name || payment.payment_mode_code}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              ₹{safeFormatCurrency(payment.amount)}
                            </Typography>
                          </Box>
                        );
                      })}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total New Payment
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          ₹{safeFormatCurrency(selectedPaymentModes.reduce((sum, p) => sum + p.amount, 0))}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/repair-orders')}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  size="large"
                  sx={{
                    background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                    borderRadius: 2,
                    px: 4,
                    py: 1
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Repair Order'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* New Customer Dialog */}
        <Dialog open={openCustomerDialog} onClose={() => setOpenCustomerDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  variant="outlined"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  variant="outlined"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  variant="outlined"
                  multiline
                  rows={2}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GSTIN"
                  variant="outlined"
                  value={newCustomer.gstin}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, gstin: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCustomerDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer} variant="contained" disabled={!newCustomer.name || !newCustomer.phone}>
              Add Customer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default EditRepairOrder;
